/**
 * Tiled map entity and path parser for Phaser.
 * Parses object layers "Paths" and "Entities" according to our schema and returns typed results with non-fatal warnings.
 */

import Phaser from 'phaser';
import { LAYERS, OBJECT_TYPES, PROPS, DEFAULTS } from './mapConstants';
import {
  Facing,
  EnemyType,
  PowerupType,
  ParsedMapEntities,
  ParsedPaths,
  ParsedPath,
  PlayerSpawnConfig,
  PowerupConfig,
  WaterBottleConfig,
  EnemyConfig,
} from '../types/MapEntities';

/**
 * Result of parsing a Tiled map for entities and paths.
 */
export interface ParseResult {
  entities: ParsedMapEntities;
  paths: ParsedPaths;
  warnings: string[];
}

/**
 * Utility for parsing entities and paths out of a Phaser.Tilemaps.Tilemap built from a Tiled map.
 *
 * Behavior:
 * - Non-fatal: collects warnings instead of throwing.
 * - Defensive: checks nullish values and types.
 * - Coordinates: uses Tiled object's x,y as world coordinates.
 * - Paths: converts polyline points (relative to object) to world coordinates.
 */
export class MapEntityParser {
  /**
   * Parse the provided Tilemap into entities and paths.
   */
  static parse(map: Phaser.Tilemaps.Tilemap): ParseResult {
    const warnings: string[] = [];

    // Initialize outputs
    const paths: ParsedPaths = {
      list: [],
      byId: new Map(),
    };

    const entities: ParsedMapEntities = {
      spawn: undefined,
      powerups: [],
      consumables: [],
      enemies: [],
    };

    // 1) Parse paths first (required by entities that may reference them)
    try {
      const pathsLayer = this.findObjectLayer(map, LAYERS.Paths);
      if (!pathsLayer) {
        warnings.push(`Paths layer "${LAYERS.Paths}" not found; proceeding with empty paths.`);
      } else {
        for (const obj of pathsLayer.objects ?? []) {
          // Only consider polyline objects
          // Tiled polyline objects in Phaser typings: type any - we access cautiously
          const anyObj = obj as any;
          const polyline: Array<{ x: number; y: number }> | undefined = anyObj?.polyline;

          if (!polyline || !Array.isArray(polyline)) {
            continue;
          }

          const propMap = toPropMap(anyObj?.properties);
          // Determine id: prefer custom property id, fallback to Tiled object's numeric id
          const customIdRaw = propMap.get(PROPS.id);
          let id: number | undefined;
          if (typeof customIdRaw === 'number') {
            id = customIdRaw;
          } else if (typeof customIdRaw === 'string' && Number.isFinite(Number(customIdRaw))) {
            id = Number(customIdRaw);
          } else if (typeof anyObj?.id === 'number') {
            id = anyObj.id;
          } else if (typeof anyObj?.id === 'string' && Number.isFinite(Number(anyObj.id))) {
            id = Number(anyObj.id);
          } else {
            id = undefined;
          }

          // Closed flag: optional boolean
          const closedRaw = propMap.get(PROPS.closed);
          const closed = typeof closedRaw === 'boolean' ? closedRaw : undefined;

          const baseX = numberOr(anyObj?.x, 0) ?? 0;
          const baseY = numberOr(anyObj?.y, 0) ?? 0;

          const points = polyline
            .filter((p) => p && typeof p.x === 'number' && typeof p.y === 'number')
            .map((p) => ({ x: baseX + p.x, y: baseY + p.y }));

          // Require numeric id for storage; if missing, warn and skip
          if (typeof id !== 'number') {
            warnings.push('Path missing numeric id; skipped.');
            continue;
          }

          const parsed: ParsedPath = { id, closed, points };

          if (paths.byId.has(id)) {
            warnings.push(`Duplicate path id "${String(id)}" encountered; keeping the first occurrence.`);
          } else {
            paths.byId.set(id, parsed);
            paths.list.push(parsed);
          }
        }
      }
    } catch (err) {
      warnings.push(`Error while parsing paths: ${toErr(err)}`);
    }

    // 2) Parse entities
    let spawnFound = false;
    try {
      const entitiesLayer = this.findObjectLayer(map, LAYERS.Entities);
      if (!entitiesLayer) {
        warnings.push(`Entities layer "${LAYERS.Entities}" not found; will fallback to center spawn.`);
      } else {
        for (const obj of entitiesLayer.objects ?? []) {
          const anyObj = obj as any;
          const typeStr = String(anyObj?.type ?? '').trim();

          const propMap = toPropMap(anyObj?.properties);
          const posX = numberOr(anyObj?.x, undefined);
          const posY = numberOr(anyObj?.y, undefined);

          // If object lacks position, warn and skip
          if (posX == null || posY == null) {
            warnings.push(`Object with missing position skipped (type="${typeStr || 'unknown'}").`);
            continue;
          }

          switch (typeStr) {
            case OBJECT_TYPES.PlayerSpawn: {
              if (spawnFound) {
                warnings.push('Multiple PlayerSpawn objects found; using the first one only.');
                break;
              }
              const facingRaw = propMap.get(PROPS.facing);
              const facing = isFacing(facingRaw) ? (facingRaw as Facing) : undefined;

              entities.spawn = { x: posX, y: posY, facing } as PlayerSpawnConfig;
              spawnFound = true;
              break;
            }

            case OBJECT_TYPES.Powerup: {
              const powerupTypeRaw = propMap.get(PROPS.powerupType);
              const powerupType = (typeof powerupTypeRaw === 'string' ? powerupTypeRaw : 'Pistol') as PowerupType;

              if (powerupType !== 'Pistol') {
                warnings.push(`Unsupported Powerup type "${String(powerupType)}"; only "Pistol" is accepted. Skipped.`);
                break;
              }

              const ammoRaw = propMap.get(PROPS.ammo);
              const ammo = numberOr(ammoRaw, DEFAULTS.pistolAmmo);

              const respawnRaw = propMap.get(PROPS.respawn);
              const respawn = numberIf(respawnRaw);

              const rotateRaw = propMap.get(PROPS.rotate);
              const rotate = booleanIf(rotateRaw);

              const pu: PowerupConfig = {
                type: powerupType,
                x: posX,
                y: posY,
                ammo,
                respawn,
                rotate,
              };
              entities.powerups.push(pu);
              break;
            }

            case OBJECT_TYPES.Consumable: {
              const consumableTypeRaw = propMap.get(PROPS.consumableType);
              const consumableType =
                typeof consumableTypeRaw === 'string' ? consumableTypeRaw : 'WaterBottle';

              if (consumableType !== 'WaterBottle') {
                warnings.push(`Unsupported Consumable type "${String(consumableType)}"; only "WaterBottle" is accepted. Skipped.`);
                break;
              }

              const healRaw = propMap.get(PROPS.heal);
              const heal = numberOr(healRaw, DEFAULTS.waterHeal);

              const respawnRaw = propMap.get(PROPS.respawn);
              const respawn = numberIf(respawnRaw);

              const rotateRaw = propMap.get(PROPS.rotate);
              const rotate = booleanIf(rotateRaw);

              const c: WaterBottleConfig = {
                type: 'WaterBottle',
                x: posX,
                y: posY,
                heal,
                respawn,
                rotate,
              };
              entities.consumables.push(c);
              break;
            }

            case OBJECT_TYPES.Enemy: {
              const enemyTypeRaw = propMap.get(PROPS.enemyType);
              const enemyType: EnemyType = isEnemyType(enemyTypeRaw) ? (enemyTypeRaw as EnemyType) : 'Grunt';

              // Defaults per enemy type
              const defaults = DEFAULTS.enemy?.[enemyType] ?? {};

              const speedRaw = propMap.get(PROPS.speed);
              const visionRaw = propMap.get(PROPS.vision);
              const speed = numberOr(speedRaw, numberOr(defaults.speed, undefined));
              const vision = numberOr(visionRaw, numberOr(defaults.vision, undefined));

              const patrolPathIdRaw = propMap.get(PROPS.patrolPathId);
              let patrolPathId = numberOr(patrolPathIdRaw, undefined) as number | undefined;
              if (patrolPathId != null) {
                // Only accept if exists
                if (!paths.byId.has(patrolPathId)) {
                  warnings.push(
                    `Enemy at (${posX},${posY}) references missing patrolPathId "${String(patrolPathId)}"; reference omitted.`
                  );
                  patrolPathId = undefined;
                }
              }

              const patrolLoopRaw = propMap.get(PROPS.patrolLoop);
              const patrolLoop = booleanIf(patrolLoopRaw);

              const patrolWaitRaw = propMap.get(PROPS.patrolWait);
              const patrolWait = numberIf(patrolWaitRaw);

              const enemy: EnemyConfig = {
                type: enemyType,
                x: posX,
                y: posY,
                speed,
                vision,
                patrolPathId,
                patrolLoop,
                patrolWait,
              };
              entities.enemies.push(enemy);
              break;
            }

            default: {
              if (typeStr) {
                warnings.push(`Unknown object.type "${typeStr}" encountered in Entities layer; skipped.`);
              } else {
                warnings.push(`Object with empty type encountered in Entities layer; skipped.`);
              }
              break;
            }
          }
        }
      }
    } catch (err) {
      warnings.push(`Error while parsing entities: ${toErr(err)}`);
    }

    // 3) Fallback spawn if not found
    if (!entities.spawn) {
      const centerX = computeMapCenterX(map);
      const centerY = computeMapCenterY(map);
      entities.spawn = { x: centerX, y: centerY };
      warnings.push('No PlayerSpawn found; defaulted spawn to map center.');
    }

    return { entities, paths, warnings };
  }

  /**
   * Find an object layer in the map by name.
   */
  private static findObjectLayer(
    map: Phaser.Tilemaps.Tilemap,
    layerName: string
  ): Phaser.Tilemaps.ObjectLayer | undefined {
    // Phaser's getObjectLayer returns ObjectLayer or null
    const layer = map.getObjectLayer(layerName);
    return (layer as Phaser.Tilemaps.ObjectLayer) ?? undefined;
  }
}

/**
 * Convert a Tiled properties array into a Map<name, value> for convenient access.
 * Accepts undefined/null and returns an empty Map.
 */
function toPropMap(
  props: Array<{ name: string; value: unknown }> | undefined
): Map<string, unknown> {
  const map = new Map<string, unknown>();
  if (!props || !Array.isArray(props)) return map;
  for (const p of props) {
    if (!p || typeof p.name !== 'string') continue;
    map.set(p.name, (p as any).value);
  }
  return map;
}

/**
 * Numeric fallback helper: returns n if it's a finite number, otherwise def.
 */
function numberOr(n: unknown, def: number | undefined): number | undefined {
  const val = typeof n === 'string' ? Number(n) : (n as number);
  return Number.isFinite(val as number) ? (val as number) : def;
}

/**
 * Return a number only if the input is a finite number; otherwise undefined.
 */
function numberIf(n: unknown): number | undefined {
  const val = typeof n === 'string' ? Number(n) : (n as number);
  return Number.isFinite(val as number) ? (val as number) : undefined;
}

/**
 * Return a boolean only if the input is a boolean; otherwise undefined.
 */
function booleanIf(b: unknown): boolean | undefined {
  return typeof b === 'boolean' ? b : undefined;
}

/**
 * Type guard for Facing strings.
 */
function isFacing(v: unknown): v is Facing {
  return typeof v === 'string' && (v === 'up' || v === 'down' || v === 'left' || v === 'right');
}

/**
 * Type guard for EnemyType strings.
 */
function isEnemyType(v: unknown): v is EnemyType {
  return typeof v === 'string' && (v === 'Grunt' || v === 'Fast' || v === 'Tank');
}

/**
 * Compute map center X using widthInPixels if available else width * tileWidth.
 */
function computeMapCenterX(map: Phaser.Tilemaps.Tilemap): number {
  const w =
    (map as any).widthInPixels ??
    (Number.isFinite(map.width as number) && Number.isFinite(map.tileWidth as number)
      ? (map.width as number) * (map.tileWidth as number)
      : undefined);
  return (typeof w === 'number' ? w : 0) / 2;
}

/**
 * Compute map center Y using heightInPixels if available else height * tileHeight.
 */
function computeMapCenterY(map: Phaser.Tilemaps.Tilemap): number {
  const h =
    (map as any).heightInPixels ??
    (Number.isFinite(map.height as number) && Number.isFinite(map.tileHeight as number)
      ? (map.height as number) * (map.tileHeight as number)
      : undefined);
  return (typeof h === 'number' ? h : 0) / 2;
}

/**
 * Normalize errors to string.
 */
function toErr(err: unknown): string {
  if (err instanceof Error) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}